/** @jsxImportSource theme-ui */
import React, { useRef, useState } from 'react';
import { Box, Flex, Heading, Label, Text, Textarea } from 'theme-ui';
import { Formik, Field } from 'formik';
import { JSONTree } from 'react-json-tree';
import jsonTreeTheme from '@/theme/jsonTreeTheme';
import {
    useGetWidgetConfigurationSchemasQuery,
    usePostWidgetConfigurationSchemaMutation,
    useSetWidgetConfigurationSchemaMigrationMutation,
    useDeleteWidgetConfigurationSchemaMigrationMutation,
} from '@/store/apiService';
import { Container } from '@/components/styled';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { toast } from 'react-toastify';

type View = 'list' | 'create';

const ConfigurationSchema: React.FC = () => {
    const [view, setView] = useState<View>('list');
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [rawIds, setRawIds] = useState<Set<number>>(new Set());
    const [copiedIds, setCopiedIds] = useState<Set<number>>(new Set());
    const preRefs = useRef<Map<number, HTMLElement>>(new Map());
    const [migrationEditIds, setMigrationEditIds] = useState<Set<number>>(new Set());
    const [confirmClearVersion, setConfirmClearVersion] = useState<number | null>(null);
    const { data, isLoading } = useGetWidgetConfigurationSchemasQuery({});
    const [postSchema] = usePostWidgetConfigurationSchemaMutation();
    const [setMigration] = useSetWidgetConfigurationSchemaMigrationMutation();
    const [deleteMigration, deleteMigrationState] =
        useDeleteWidgetConfigurationSchemaMigrationMutation();

    const schemas = data?.Results ?? [];
    const nextVersion = schemas.length ? Math.max(...schemas.map((s) => s.Version)) + 1 : 1;

    const toggleExpanded = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleMigrationEdit = (id: number) => {
        setMigrationEditIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleRaw = (id: number) => {
        setRawIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const copyRaw = (id: number, schema: object) => {
        navigator.clipboard.writeText(JSON.stringify(schema, null, 2)).then(() => {
            const el = preRefs.current.get(id);
            if (el) {
                const selection = window.getSelection();
                if (selection) {
                    const range = document.createRange();
                    range.selectNodeContents(el);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
            setCopiedIds((prev) => new Set(prev).add(id));
            setTimeout(() => {
                setCopiedIds((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }, 2000);
        });
    };

    if (view === 'create') {
        return (
            <Container>
                <Flex sx={{ alignItems: 'center', gap: 2, mb: 3 }}>
                    <Button variant="secondary" type="button" onClick={() => setView('list')}>
                        Back
                    </Button>
                    <Heading as="h2">Create configuration schema</Heading>
                </Flex>
                <Formik
                    initialValues={{
                        version: nextVersion,
                        schemaJson: '',
                        defaultConfigurationJson: '',
                    }}
                    onSubmit={(values, { setErrors, setSubmitting }) => {
                        let parsed: Record<string, unknown>;
                        try {
                            parsed = JSON.parse(values.schemaJson);
                        } catch {
                            setErrors({ schemaJson: 'Invalid JSON' });
                            setSubmitting(false);
                            return;
                        }
                        const trimmedDefaultConfiguration = values.defaultConfigurationJson.trim();
                        if (trimmedDefaultConfiguration) {
                            try {
                                JSON.parse(trimmedDefaultConfiguration);
                            } catch {
                                setErrors({ defaultConfigurationJson: 'Invalid JSON' });
                                setSubmitting(false);
                                return;
                            }
                        }
                        postSchema({
                            Version: values.version,
                            Schema: parsed as { [key: string]: { [key: string]: unknown } },
                            ...(trimmedDefaultConfiguration
                                ? { DefaultConfiguration: trimmedDefaultConfiguration }
                                : {}),
                        })
                            .unwrap()
                            .then(() => {
                                setView('list');
                            })
                            .catch((err) => {
                                setSubmitting(false);
                                toast.error(err?.data ?? 'Failed to create schema', {
                                    autoClose: 5000,
                                });
                            });
                    }}
                >
                    {(formProps) => (
                        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                            <GrayFormikField
                                type="number"
                                name="version"
                                label="Version"
                                required
                                placeholder="1"
                            />
                            <Field
                                name="schemaJson"
                                validate={(value: string) => {
                                    if (!value.trim()) return 'Required';
                                    try {
                                        JSON.parse(value);
                                    } catch {
                                        return 'Invalid JSON';
                                    }
                                }}
                            >
                                {({ field, meta }: any) => (
                                    <Box mb="2" sx={{ width: '100%' }}>
                                        <Label>
                                            Schema (JSON)
                                            <Text color="danger" ml="1">
                                                *
                                            </Text>
                                        </Label>
                                        <Textarea
                                            {...field}
                                            backgroundColor="gray.1"
                                            rows={15}
                                            sx={{
                                                maxWidth: '100%',
                                                minWidth: '100%',
                                                fontFamily: 'monospace',
                                                fontSize: 1,
                                            }}
                                            placeholder={
                                                '{\n  "type": "object",\n  "properties": {}\n}'
                                            }
                                        />
                                        {meta.touched && meta.error && (
                                            <Text color="danger">{meta.error}</Text>
                                        )}
                                    </Box>
                                )}
                            </Field>
                            <Field
                                name="defaultConfigurationJson"
                                validate={(value: string) => {
                                    if (!value.trim()) return undefined;
                                    try {
                                        JSON.parse(value);
                                    } catch {
                                        return 'Invalid JSON';
                                    }
                                }}
                            >
                                {({ field, meta }: any) => (
                                    <Box mb="2" sx={{ width: '100%' }}>
                                        <Label>Default configuration (JSON, optional)</Label>
                                        <Textarea
                                            {...field}
                                            backgroundColor="gray.1"
                                            rows={10}
                                            sx={{
                                                maxWidth: '100%',
                                                minWidth: '100%',
                                                fontFamily: 'monospace',
                                                fontSize: 1,
                                            }}
                                            placeholder={'{\n  "someField": "someValue"\n}'}
                                        />
                                        {meta.touched && meta.error && (
                                            <Text color="danger">{meta.error}</Text>
                                        )}
                                    </Box>
                                )}
                            </Field>
                            <Flex sx={{ justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={() => setView('list')}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    isLoading={formProps.isSubmitting}
                                    disabled={formProps.isSubmitting}
                                    onClick={formProps.submitForm}
                                >
                                    Save
                                </Button>
                            </Flex>
                        </Flex>
                    )}
                </Formik>
            </Container>
        );
    }

    return (
        <Container>
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Heading as="h2">Configuration schemas</Heading>
                <Button onClick={() => setView('create')}>Create schema</Button>
            </Flex>
            {isLoading && <Text>Loading...</Text>}
            {!isLoading && schemas.length === 0 && <Text>No configuration schemas found.</Text>}
            {schemas.map((schema) => {
                const expanded = expandedIds.has(schema.Id);
                return (
                    <Box
                        key={schema.Id}
                        sx={{
                            mb: 3,
                            borderRadius: 'sm',
                            backgroundColor: 'gray.1',
                            overflow: 'hidden',
                        }}
                    >
                        <Flex
                            sx={{
                                gap: 4,
                                p: 3,
                                cursor: 'pointer',
                                alignItems: 'center',
                                '&:hover': { backgroundColor: 'gray.2' },
                            }}
                            onClick={() => toggleExpanded(schema.Id)}
                        >
                            <Text sx={{ fontWeight: 'bold' }}>Id: {schema.Id}</Text>
                            <Text sx={{ fontWeight: 'bold' }}>Version: {schema.Version}</Text>
                            <Text sx={{ color: 'gray.6', fontSize: 1 }}>
                                Created: {schema.CreatedDate}
                            </Text>
                            <Text sx={{ ml: 'auto', fontSize: 1, color: 'gray.5' }}>
                                {expanded ? '▲' : '▼'}
                            </Text>
                        </Flex>
                        {expanded && (
                            <Box sx={{ px: 3, pb: 3 }}>
                                <Flex sx={{ justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                                    {rawIds.has(schema.Id) && (
                                        <Button
                                            variant="secondary"
                                            type="button"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                copyRaw(schema.Id, schema.Schema ?? {});
                                            }}
                                        >
                                            {copiedIds.has(schema.Id) ? 'Copied!' : 'Copy'}
                                        </Button>
                                    )}
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            toggleRaw(schema.Id);
                                        }}
                                    >
                                        {rawIds.has(schema.Id) ? 'Tree' : 'Raw'}
                                    </Button>
                                </Flex>
                                {rawIds.has(schema.Id) ? (
                                    <Box
                                        as="pre"
                                        ref={(el: HTMLElement | null) => {
                                            if (el) preRefs.current.set(schema.Id, el);
                                            else preRefs.current.delete(schema.Id);
                                        }}
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: 1,
                                            backgroundColor: 'gray.2',
                                            borderRadius: 'sm',
                                            p: 2,
                                            overflowX: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                        }}
                                    >
                                        {JSON.stringify(schema.Schema ?? {}, null, 2)}
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            '& > ul': {
                                                padding: (t: any) => `${t.space[2]} !important`,
                                                borderRadius: (t: any) =>
                                                    `${t.radii['sm']} !important`,
                                            },
                                        }}
                                    >
                                        <JSONTree
                                            data={schema.Schema ?? {}}
                                            hideRoot={true}
                                            theme={jsonTreeTheme}
                                            invertTheme={true}
                                            shouldExpandNodeInitially={(_keyPath, _data, level) =>
                                                level === 0
                                            }
                                        />
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        mt: 3,
                                        pt: 3,
                                        borderTop: '1px solid',
                                        borderColor: 'gray.3',
                                    }}
                                >
                                    <Heading as="h4" sx={{ fontSize: 2, mb: 2 }}>
                                        Default configuration
                                    </Heading>
                                    {schema.DefaultConfiguration ? (
                                        <Box
                                            as="pre"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: 1,
                                                backgroundColor: 'gray.2',
                                                borderRadius: 'sm',
                                                p: 2,
                                                overflowX: 'auto',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            {(() => {
                                                try {
                                                    return JSON.stringify(
                                                        JSON.parse(schema.DefaultConfiguration),
                                                        null,
                                                        2
                                                    );
                                                } catch {
                                                    return schema.DefaultConfiguration;
                                                }
                                            })()}
                                        </Box>
                                    ) : (
                                        <Text sx={{ color: 'gray.6', fontSize: 1 }}>None set.</Text>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        mt: 3,
                                        pt: 3,
                                        borderTop: '1px solid',
                                        borderColor: 'gray.3',
                                    }}
                                >
                                    <Heading as="h4" sx={{ fontSize: 2, mb: 2 }}>
                                        Migration patch
                                    </Heading>

                                    {confirmClearVersion === schema.Version && (
                                        <ConfirmationModal
                                            hideOnClickOutside
                                            loading={deleteMigrationState.isLoading}
                                            headingText={`Clear migration patch for version ${schema.Version}?`}
                                            content={`Remove the migration patch for version ${schema.Version}? Bump-version with UseFactoryDefaults=false will no longer work for this version until a new patch is set.`}
                                            onNo={() => setConfirmClearVersion(null)}
                                            onYes={() => {
                                                deleteMigration({ Version: schema.Version })
                                                    .unwrap()
                                                    .then(() => setConfirmClearVersion(null))
                                                    .catch((err) => {
                                                        setConfirmClearVersion(null);
                                                        toast.error(
                                                            err?.data?.ResponseStatus?.Message ??
                                                                'Failed to clear migration patch',
                                                            { autoClose: 5000 }
                                                        );
                                                    });
                                            }}
                                        />
                                    )}

                                    {migrationEditIds.has(schema.Id) ? (
                                        <Formik
                                            key={`migration-${schema.Id}`}
                                            initialValues={{
                                                patch: schema.MigrationFromPreviousVersion ?? '',
                                            }}
                                            onSubmit={(values, { setErrors, setSubmitting }) => {
                                                try {
                                                    JSON.parse(values.patch);
                                                } catch {
                                                    setErrors({ patch: 'Invalid JSON' });
                                                    setSubmitting(false);
                                                    return;
                                                }
                                                setMigration({
                                                    Version: schema.Version,
                                                    Patch: values.patch,
                                                })
                                                    .unwrap()
                                                    .then(() => {
                                                        setSubmitting(false);
                                                        toggleMigrationEdit(schema.Id);
                                                    })
                                                    .catch((err) => {
                                                        setSubmitting(false);
                                                        toast.error(
                                                            err?.data?.ResponseStatus?.Message ??
                                                                'Failed to set migration patch',
                                                            { autoClose: 5000 }
                                                        );
                                                    });
                                            }}
                                        >
                                            {(formProps) => (
                                                <Flex sx={{ flexDirection: 'column', gap: 2 }}>
                                                    <Field
                                                        name="patch"
                                                        validate={(value: string) => {
                                                            if (!value.trim()) return 'Required';
                                                            try {
                                                                JSON.parse(value);
                                                            } catch {
                                                                return 'Invalid JSON';
                                                            }
                                                        }}
                                                    >
                                                        {({ field, meta }: any) => (
                                                            <Box mb="2" sx={{ width: '100%' }}>
                                                                <Label>
                                                                    Patch (RFC 6902 JSON, as a
                                                                    string)
                                                                    <Text color="danger" ml="1">
                                                                        *
                                                                    </Text>
                                                                </Label>
                                                                <Textarea
                                                                    {...field}
                                                                    backgroundColor="gray.1"
                                                                    rows={8}
                                                                    sx={{
                                                                        maxWidth: '100%',
                                                                        minWidth: '100%',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: 1,
                                                                    }}
                                                                    placeholder={
                                                                        '[{ "op": "add", "path": "/newField", "value": "..." }]'
                                                                    }
                                                                />
                                                                {meta.touched && meta.error && (
                                                                    <Text color="danger">
                                                                        {meta.error}
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Field>
                                                    <Flex
                                                        sx={{ justifyContent: 'flex-end', gap: 2 }}
                                                    >
                                                        <Button
                                                            variant="secondary"
                                                            type="button"
                                                            onClick={() =>
                                                                toggleMigrationEdit(schema.Id)
                                                            }
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            type="submit"
                                                            isLoading={formProps.isSubmitting}
                                                            disabled={formProps.isSubmitting}
                                                            onClick={formProps.submitForm}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Flex>
                                                </Flex>
                                            )}
                                        </Formik>
                                    ) : (
                                        <Flex sx={{ flexDirection: 'column', gap: 2 }}>
                                            <Text
                                                sx={{
                                                    color: 'gray.6',
                                                    fontSize: 1,
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                {schema.MigrationFromPreviousVersion
                                                    ? schema.MigrationFromPreviousVersion
                                                    : 'No migration patch set.'}
                                            </Text>
                                            <Flex
                                                sx={{
                                                    gap: 2,
                                                    flexWrap: 'wrap',
                                                    '& > button': { flexShrink: 0 },
                                                }}
                                            >
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => toggleMigrationEdit(schema.Id)}
                                                >
                                                    Set migration patch
                                                </Button>
                                                {schema.MigrationFromPreviousVersion && (
                                                    <Button
                                                        variant="danger"
                                                        type="button"
                                                        onClick={() =>
                                                            setConfirmClearVersion(schema.Version)
                                                        }
                                                    >
                                                        Clear migration patch
                                                    </Button>
                                                )}
                                            </Flex>
                                        </Flex>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Container>
    );
};

export default ConfigurationSchema;
